import { FormEvent, useState } from 'react';
import { dealerErrorFields, dealerErrorMessage, dealersApi, isFeatureOff } from '../../services/dealersApi';
import { EMIRATES } from '../../types/delivery';
import { Showroom, ShowroomRequest } from '../../types/dealers';
import MapPicker from '../MapPicker';
import { describedBy, FormField } from '../promotions/PromoFormParts';

interface ShowroomFormProps {
  dealerId: string;
  /** null = add (at most 20 active, 422 SHOWROOM_LIMIT). */
  showroom: Showroom | null;
  token: string;
  onClose: () => void;
  onSaved: (saved: Showroom) => void;
  onFeatureOff: () => void;
}

/** Spec 085 D7 — a dealer showroom: label, address, emirate and a map pin (pickup / drop-off point). */
export default function ShowroomForm({ dealerId, showroom, token, onClose, onSaved, onFeatureOff }: ShowroomFormProps) {
  const editing = showroom !== null;
  const [v, setV] = useState({
    label: showroom?.label ?? '',
    street: showroom?.street ?? '',
    building: showroom?.building ?? '',
    area: showroom?.area ?? '',
    city: showroom?.city ?? '',
    emirate: showroom?.emirate ?? '',
    instructions: showroom?.instructions ?? '',
  });
  const [pin, setPin] = useState<{ lat: number; lng: number } | null>(
    showroom?.latitude != null && showroom?.longitude != null ? { lat: Number(showroom.latitude), lng: Number(showroom.longitude) } : null,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const set = (key: keyof typeof v, value: string) => setV((prev) => ({ ...prev, [key]: value }));
  const blank = (s: string) => s.trim() || null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setFormError(null);
    if (!v.label.trim()) { setErrors({ label: 'Enter a label, e.g. "Al Quoz showroom"' }); return; }
    setErrors({});
    const body: ShowroomRequest = {
      label: v.label.trim(),
      street: blank(v.street),
      building: blank(v.building),
      area: blank(v.area),
      city: blank(v.city),
      emirate: blank(v.emirate),
      instructions: blank(v.instructions),
      latitude: pin?.lat ?? null,
      longitude: pin?.lng ?? null,
    };
    setSaving(true);
    try {
      const saved = showroom
        ? await dealersApi.updateShowroom(dealerId, showroom.id, body, token)
        : await dealersApi.createShowroom(dealerId, body, token);
      onSaved(saved);
    } catch (err) {
      if (isFeatureOff(err)) { onFeatureOff(); onClose(); return; }
      const fields = dealerErrorFields(err);
      if (Object.keys(fields).length) setErrors(fields);
      setFormError(dealerErrorMessage(err, 'Failed to save the showroom'));
    } finally {
      setSaving(false);
    }
  };

  const text = (key: keyof typeof v, label: string, maxLength: number) => (
    <FormField id={`showroom-${key}`} label={label} error={errors[key]}>
      <input id={`showroom-${key}`} className="admin-form-input" value={v[key]} maxLength={maxLength} disabled={saving}
        {...describedBy(`showroom-${key}`, errors[key])} onChange={(e) => set(key, e.target.value)} />
    </FormField>
  );

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" role="dialog" aria-modal="true" aria-label={editing ? `Edit ${showroom.label}` : 'Add showroom'} style={{ maxWidth: '640px', width: '100%' }}>
        <div className="admin-modal-header">
          <h3 style={{ margin: 0 }}>{editing ? `Edit ${showroom.label}` : 'Add showroom'}</h3>
          <button type="button" className="admin-modal-close" onClick={onClose} disabled={saving} aria-label="Close">&times;</button>
        </div>
        <form onSubmit={submit} noValidate>
          <div className="admin-modal-body">
            {formError && <div className="admin-info-banner danger" role="alert" data-testid="showroom-form-error">{formError}</div>}
            {text('label', 'Label', 100)}
            <div className="dealer-form-grid">
              {text('street', 'Street', 255)}
              {text('building', 'Building', 100)}
              {text('area', 'Area', 100)}
              {text('city', 'City', 100)}
              <FormField id="showroom-emirate" label="Emirate" error={errors.emirate}>
                <select id="showroom-emirate" className="admin-form-input" value={v.emirate} disabled={saving} onChange={(e) => set('emirate', e.target.value)}>
                  <option value="">—</option>
                  {EMIRATES.map((em) => <option key={em.code} value={em.code}>{em.label}</option>)}
                </select>
              </FormField>
            </div>
            <FormField id="showroom-instructions" label="Instructions for drivers (optional)" error={errors.instructions}>
              <textarea id="showroom-instructions" className="admin-form-input" rows={2} value={v.instructions} maxLength={500} disabled={saving}
                onChange={(e) => set('instructions', e.target.value)} />
            </FormField>
            <div className="admin-form-group">
              <span className="admin-form-label">Map pin</span>
              <MapPicker latitude={pin?.lat ?? null} longitude={pin?.lng ?? null} onChange={(lat, lng) => setPin({ lat, lng })}
                searchPlaceholder="Search the showroom address" />
              {(errors.latitude || errors.longitude) && (
                <div role="alert" className="dealer-field-error">{errors.latitude ?? errors.longitude}</div>
              )}
            </div>
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn admin-btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add showroom'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
